import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { X, Plus, Loader2, Edit2, Check } from "lucide-react";

const AddEditQuestionModal = ({
  open = true,
  onOpenChange = () => {},
  question = null,
  onSubmit = (data) => console.log("Submitted:", data),
  questionTypes = {},
  categories = [],
  questionBanks = {},
  isLoading = false,
  selectedQuestionBankId = null,
}) => {
  const combinedQuestionBanks = [
    ...(questionBanks?.banks || []),
    ...(questionBanks?.companyQuestionBanks || []),
    ...(questionBanks?.purchasedQuestionBanks || []),
  ];

  const [choices, setChoices] = useState([]);
  const [newChoice, setNewChoice] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [editingChoiceIndex, setEditingChoiceIndex] = useState(null);
  const [editingChoiceValue, setEditingChoiceValue] = useState("");
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    question: "",
    questionType: "",
    category: "",
    subCategory: "",
    questionBankId: selectedQuestionBankId || "",
    value: 1,
    difficultyLevel: "",
    answers: [],
  });

  const isEditing = !!question;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question.trim()) newErrors.question = "Question is required";
    if (!formData.questionType)
      newErrors.questionType = "Question type is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.subCategory)
      newErrors.subCategory = "Sub category is required";
    if (!formData.questionBankId)
      newErrors.questionBankId = "Question bank is required";
    if (!formData.value || formData.value < 1)
      newErrors.value = "Points must be at least 1";
    if (!formData.difficultyLevel)
      newErrors.difficultyLevel = "Difficulty level is required";
    if (choices.length > 0 && formData.answers.length === 0) {
      newErrors.answers = "Please select at least one correct answer";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (question) {
      setFormData({
        question: question.question || "",
        questionType: question.questionType || question.qtyp || "",
        category: question.category || "",
        subCategory: question.subCategory || "",
        questionBankId: question.questionBankId || selectedQuestionBankId || "",
        value: question.value || 1,
        difficultyLevel: question.difficultyLevel || "",
        answers: question.answers || [],
      });
      setChoices(question.choices || []);
      setTags(question.tags || []);
    } else {
      // Pre-populate from selected question bank
      if (selectedQuestionBankId) {
        const selectedBank = combinedQuestionBanks.find(
          (qb) => qb._id === selectedQuestionBankId
        );
        if (selectedBank) {
          setFormData((prev) => ({
            ...prev,
            category: selectedBank.category,
            subCategory: selectedBank.subcategory,
            questionType: selectedBank.questionType,
            questionBankId: selectedQuestionBankId,
          }));
        }
      }
    }
  }, [question, selectedQuestionBankId, questionBanks]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleQuestionBankChange = (value) => {
    handleInputChange("questionBankId", value);
    const selectedBank = combinedQuestionBanks.find((qb) => qb._id === value);
    if (selectedBank) {
      handleInputChange("category", selectedBank.category);
      handleInputChange("subCategory", selectedBank.subcategory);
      handleInputChange("questionType", selectedBank.questionType);
    }
  };

  const handleAddChoice = () => {
    if (newChoice.trim() && !choices.includes(newChoice.trim())) {
      setChoices([...choices, newChoice.trim()]);
      setNewChoice("");
    }
  };

  const handleRemoveChoice = (index) => {
    const removedChoice = choices[index];
    setChoices(choices.filter((_, i) => i !== index));

    // Remove from answers if it was selected
    const newAnswers = formData.answers.filter(
      (answer) => answer !== removedChoice
    );
    handleInputChange("answers", newAnswers);
  };

  const handleEditChoice = (index) => {
    setEditingChoiceIndex(index);
    setEditingChoiceValue(choices[index]);
  };

  const handleSaveEditChoice = () => {
    if (editingChoiceValue.trim() && editingChoiceIndex !== null) {
      const oldChoice = choices[editingChoiceIndex];
      const newChoices = [...choices];
      newChoices[editingChoiceIndex] = editingChoiceValue.trim();
      setChoices(newChoices);

      // Update answers if the edited choice was selected
      const newAnswers = formData.answers.map((answer) =>
        answer === oldChoice ? editingChoiceValue.trim() : answer
      );
      handleInputChange("answers", newAnswers);

      setEditingChoiceIndex(null);
      setEditingChoiceValue("");
    }
  };

  const handleCancelEditChoice = () => {
    setEditingChoiceIndex(null);
    setEditingChoiceValue("");
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleAnswerChange = (choice, checked, isRadio = false) => {
    let newAnswers;
    if (isRadio) {
      newAnswers = [choice];
    } else {
      newAnswers = checked
        ? [...formData.answers, choice]
        : formData.answers.filter((answer) => answer !== choice);
    }
    handleInputChange("answers", newAnswers);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (validateForm()) {
    const questionData = {
      ...formData,
      qtyp: String(formData.questionType), // Convert array to string
      choices,
      tags,
    };
    
    try {
      await onSubmit(questionData);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        alert(error.response.data.message);
      } else {
        console.error("Error submitting question:", error);
        alert("An error occurred while submitting the question");
      }
    }
  }
};

  const handleClose = () => {
    onOpenChange(false);
    setFormData({
      question: "",
      questionType: "",
      category: "",
      subCategory: "",
      questionBankId: selectedQuestionBankId || "",
      value: 1,
      difficultyLevel: "",
      answers: [],
    });
    setChoices([]);
    setTags([]);
    setNewChoice("");
    setNewTag("");
    setErrors({});
    setEditingChoiceIndex(null);
    setEditingChoiceValue("");
  };

  const selectedQuestionType = Array.isArray(formData.questionType)
    ? questionTypes?.questionTypes?.filter((qt) =>
        formData.questionType.includes(qt.identifier)
      )
    : formData.questionType || [];

  const isMultipleChoice =
    selectedQuestionType[0]?.identifier ||
    selectedQuestionType === "MULTIPLE_CHOICE";
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Question" : "Add New Question"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the question details."
              : "Create a new question for the question bank."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="questionBankId">Question Bank *</Label>
                <Select
                  value={formData.questionBankId}
                  onValueChange={handleQuestionBankChange}
                  disabled={!!selectedQuestionBankId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Question Bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {combinedQuestionBanks.map((bank) => (
                      <SelectItem key={bank._id} value={bank._id}>
                        {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.questionBankId && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.questionBankId}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="questionType">Question Type *</Label>
                <Input
                  id="questionType"
                  name="questionType"
                  value={formData.questionType}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                  placeholder="Question type will be auto-populated"
                />
                {errors.questionType && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.questionType}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Input
                  id="category"
                  name="category"
                  value={formData.category}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                  placeholder="Category will be auto-populated"
                />
                {errors.category && (
                  <p className="text-sm text-red-500 mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <Label htmlFor="subCategory">Sub Category *</Label>
                <Input
                  id="subCategory"
                  name="subCategory"
                  value={formData.subCategory}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                  placeholder="Sub category will be auto-populated"
                />
                {errors.subCategory && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.subCategory}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="value">Points *</Label>
                  <Input
                    id="value"
                    name="value"
                    type="number"
                    min="1"
                    value={formData.value}
                    onChange={(e) =>
                      handleInputChange("value", parseInt(e.target.value) || 1)
                    }
                  />
                  {errors.value && (
                    <p className="text-sm text-red-500 mt-1">{errors.value}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="difficultyLevel">Difficulty *</Label>
                  <Select
                    value={formData.difficultyLevel}
                    onValueChange={(value) =>
                      handleInputChange("difficultyLevel", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.difficultyLevel && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.difficultyLevel}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="question">Question *</Label>
                <Textarea
                  id="question"
                  name="question"
                  value={formData.question}
                  onChange={(e) =>
                    handleInputChange("question", e.target.value)
                  }
                  placeholder="Enter your question"
                  rows={4}
                />
                {errors.question && (
                  <p className="text-sm text-red-500 mt-1">{errors.question}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddTag())
                      }
                    />
                    <Button type="button" onClick={handleAddTag} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {tag}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => handleRemoveTag(tag)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Answer Choices */}
          <div className="space-y-4">
            <div>
              <Label>Answer Choices</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Add an answer choice"
                    value={newChoice}
                    onChange={(e) => setNewChoice(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleAddChoice())
                    }
                  />
                  <Button type="button" onClick={handleAddChoice} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {choices.length > 0 && (
              <div>
                <Label>Select Correct Answer(s)</Label>
                {errors.answers && (
                  <p className="text-sm text-red-500 mt-1">{errors.answers}</p>
                )}
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-4">
                  {isMultipleChoice ? (
                    choices.map((choice, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between space-x-2"
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <Checkbox
                            id={`choice-${index}`}
                            checked={formData.answers.includes(choice)}
                            onCheckedChange={(checked) =>
                              handleAnswerChange(choice, checked)
                            }
                          />
                          {editingChoiceIndex === index ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                value={editingChoiceValue}
                                onChange={(e) =>
                                  setEditingChoiceValue(e.target.value)
                                }
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSaveEditChoice();
                                  } else if (e.key === "Escape") {
                                    handleCancelEditChoice();
                                  }
                                }}
                                className="flex-1"
                                autoFocus
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleSaveEditChoice}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleCancelEditChoice}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <label
                              htmlFor={`choice-${index}`}
                              className="flex-1"
                            >
                              {choice}
                            </label>
                          )}
                        </div>
                        {editingChoiceIndex !== index && (
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditChoice(index)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveChoice(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <RadioGroup
                      value={formData.answers[0] || ""}
                      onValueChange={(value) =>
                        handleAnswerChange(value, true, true)
                      }
                    >
                      {choices.map((choice, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between space-x-2"
                        >
                          <div className="flex items-center space-x-2 flex-1">
                            <RadioGroupItem
                              value={choice}
                              id={`radio-choice-${index}`}
                            />
                            {editingChoiceIndex === index ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  value={editingChoiceValue}
                                  onChange={(e) =>
                                    setEditingChoiceValue(e.target.value)
                                  }
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleSaveEditChoice();
                                    } else if (e.key === "Escape") {
                                      handleCancelEditChoice();
                                    }
                                  }}
                                  className="flex-1"
                                  autoFocus
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleSaveEditChoice}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelEditChoice}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <label
                                htmlFor={`radio-choice-${index}`}
                                className="flex-1"
                              >
                                {choice}
                              </label>
                            )}
                          </div>
                          {editingChoiceIndex !== index && (
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditChoice(index)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveChoice(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Update Question" : "Create Question"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditQuestionModal;
